import { useEffect, useMemo, useState } from "react";
import Button from "react-bootstrap/Button";
import Modal from "react-bootstrap/Modal";
import Table from "react-bootstrap/Table";
import { formatEventKickoff, formatPlacedAt } from "../lib/dateFormat";
import { cancelCallbetOrders, getgameDetails, updateCallbetDetails } from "../actions/gameSettings";
import { useDispatch, useSelector } from "react-redux";
import { toastAlert } from "../lib/toastAlert";
import {
    computeWagerFinancials,
    formatMoney,
} from "../lib/wagerFinancials";
import { isDTS } from "../../utility/platform";
import {
  getBuyPointsLabel,
  getEntryTypeLabel,
  getTerm,
} from "../../utility/complianceCopy";
import { entryDetailsTitle } from "../../utility/entryCopy";

function WagerEditModal({ show, handleClose, item, refreshData }) {
    const accountData = useSelector((state) => state.account);
    const [currentPoint, setcurrentpoint] = useState(item?.point);
    const [currentPrice, setCurrentPrice] = useState(item?.price);
    const isMoneyline = item?.oddType === "h2h";
    const dispatch = useDispatch();
    useEffect(() => {
        console.log("ItemsOfWagermodel", item)
        fetchAllOrders();
    }, [show])

    const fetchAllOrders = async () => {
        try {
            setcurrentpoint('loading..')
            setCurrentPrice('loading..')
            const { status, result } = await getgameDetails(
                { email: accountData.emailId, item: item },
                dispatch
            );
            if (result) {
                const outcomes = result.outcomes.find(e => e?.name === item?.teamName)
                if (outcomes) {
                    if (outcomes.point != null) {
                        setcurrentpoint(outcomes.point);
                    } else {
                        setcurrentpoint("-");
                    }
                    setCurrentPrice(outcomes.price);
                }
            }

        } catch (err) {
            console.log("err", err);
        }
    };

    const handleonclickevents = async (name) => {
        try {
            if (name === "UpdateOrder") {
                const risk = Number(formData.riskAmount);
                const win = Number(formData.winAmount);
                const payout = Number(formData.totalPayout);

                if (!risk || risk <= 0) {
                    return toastAlert("error", "Please enter a valid Risk Amount");
                }
                if (!win || win < 0) {
                    return toastAlert("error", "Win Amount cannot be negative");
                }

        
                if (item.betstatus !== "NotActivated") {
                    return toastAlert("error", `Cannot update order. Current status: ${item.betstatus}`);
                }

                const updatePayload = {
                    dataId: item._id,
                    riskAmount: risk,
                    winamount: win,
                    payout: payout,
                };

                if (isMoneyline) {
                    updatePayload.desiredPrice = Number(tableData[0]?.price ?? item.desiredPrice ?? item.price);
                    updatePayload.price = updatePayload.desiredPrice;
                } else {
                    updatePayload.point = tableData[0]?.point !== undefined ? Number(tableData[0].point) : item.point;
                }

                try {
                    const response = await updateCallbetDetails(updatePayload);
                    if (response?.error) throw new Error(response.message);

                    await refreshData();
                    toastAlert("success", "Order updated successfully");
                } catch (err) {
                    return toastAlert("error", err.message || "Update failed. Please try again.");
                }

            } else if (name === "OrderCancel") {
                // 1. Verification Validation
                if (!item._id) {
                    return toastAlert("error", "Invalid Order ID");
                }

                // 2. Status Check
                if (item.betstatus === "Activated") {
                    return toastAlert(
                        "error",
                        isDTS()
                            ? `Cannot cancel an already activated ${getTerm("entry", { case: "lower" })}.`
                            : "Cannot cancel an already activated bet."
                    );
                }

                try {
                    await cancelCallbetOrders({ dataId: item._id });
                    await refreshData();
                    toastAlert("success", "Order cancelled successfully");
                } catch (err) {
                    return toastAlert("error", "Cancellation failed.");
                }
            }

            await fetchAllOrders();
            handleClose();

        } catch (err) {
            console.error("Click Event Error:", err);
            toastAlert("error", "An unexpected error occurred.");
        }
    };

    const getRiskAmount = () => {
        if (item.betType === "Straight") {
            return item.wagerAmount;
        } else if (item.betType === "Parlay") {
            return item.wagerAmount;
        } else if (item.betType === "Teaser") {
            return item.wagerAmount;
        } else {
            return item.totalLossamount || 0;
        }
    };

    const getWinAmount = () => {
        if (item.betType === "Straight") {
            return item.payOut;
        } else if (item.betType === "Parlay") {
            return item.payOut;
        } else if (item.betType === "Teaser") {
            return item.payOut;
        } else {
            return item.payOut || 0;
        }
    };


    const [formData, setFormData] = useState({
        betType: "",
        riskAmount: 0,
        winAmount: 0,
        totalPayout: 0
    });

    const buildFormFromAmounts = (risk, grossPayout) => {
        const financials = computeWagerFinancials({
            wagerAmount: risk,
            payOut: grossPayout,
            betType: item?.betType,
        });
        return {
            riskAmount: financials.riskAmount,
            winAmount: financials.winAmount,
            totalPayout: financials.totalPayout,
        };
    };

    const [tableData, setTableData] = useState([]);

    useEffect(() => {
        if (!show) return;

        const risk = getRiskAmount();
        const payout = getWinAmount();

        setFormData((prev) => ({
            ...prev,
            betType: item.betType || "",
            ...buildFormFromAmounts(Number(risk), Number(payout)),
        }));

        if (item.betType === "Straight") {
            const targetPrice = item.desiredPrice || item.price;
            const targetPoint = item.point;
            setTableData([
                { price: targetPrice, point: targetPoint }
            ]);
        } else {
            setTableData(
                item.bettingData?.map(bet => ({
                    price: bet.price,
                    point: bet.point
                })) || []
            );
        }

    }, [item, show]);

    const showPtsPurchased = item?.betType === "Straight";

    const ptsPurchasedLabel = useMemo(() => {
        if (!item || !showPtsPurchased) return "0.00";
        return formatMoney(item.buyPoints ?? 0);
    }, [item, showPtsPurchased]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: Number(value) || value
        }));
    };

    const handleTableChange = (index, field, value) => {
        setTableData(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [field]: value };
            return updated;
        });
    };

    const straightpayOutCalc = (value, odds, mode) => {
        const amount = Number(value) || 0;

        let risk = formData.riskAmount;
        let win = formData.winAmount;

        if (mode === "risk") {
            risk = amount;
            win = calcWinFromRisk(risk, odds);

        }

        if (mode === "win") {
            win = amount;
            risk = calcRiskFromWin(win, odds);
        }

        const grossPayout = Number(risk) + Number(win);
        setFormData((prev) => ({
            ...prev,
            ...buildFormFromAmounts(Number(risk), grossPayout),
        }));
    };

    const calcWinFromRisk = (risk, odds) => {
        if (!risk || !odds) return 0;
        return odds > 0
            ? (risk * odds) / 100
            : (risk * 100) / Math.abs(odds);
    };

    const calcRiskFromWin = (win, odds) => {
        if (!win || !odds) return 0;
        return odds > 0
            ? (win * 100) / odds
            : (win * Math.abs(odds)) / 100;
    };

    if (!item) return null;

    return (
        <div className="wager_popup">
            <Modal
                show={show}
                onHide={handleClose}
                centered
                size="xl"
                className="wager_modal"
            >
                <Modal.Header closeButton>
                    <Modal.Title>
                        {entryDetailsTitle()}{item.ticketId}
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    <div className="row wager_details">

                        <div className="col-xl-6 cols">
                            <div className="row">
                                <div className="col-4">
                                    <span className="heading">
                                        {isDTS() ? `${getTerm("entry")} Type` : "Wager Type"}
                                    </span>
                                </div>
                                <div className="col-8">
                                    <input
                                        type="text"
                                        className="content"
                                        name="betType"
                                        value={getEntryTypeLabel(formData.betType, isDTS())}
                                        onChange={handleChange}
                                        disabled
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Date Placed */}
                        <div className="col-xl-6 cols">
                            <div className="row">
                                <div className="col-4">
                                    <span className="heading">Date Placed</span>
                                </div>
                                <div className="col-8">
                                    <input
                                        type="text"
                                        className="content"
                                        readOnly
                                        value={formatPlacedAt(item.createdAt)}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Risk Amount */}
                        <div className="col-xl-6 cols">
                            <div className="row">
                                <div className="col-4">
                                    <span className="heading">Edit Risk Amount $</span>
                                </div>
                                <div className="col-8">
                                    <input
                                        type="number"
                                        className="content"
                                        name="riskAmount"
                                        value={formData.riskAmount > 0 ? formData.riskAmount : ""}
                                        onChange={(e) =>
                                            straightpayOutCalc(
                                                e.target.value,
                                                item.price,
                                                "risk"
                                            )
                                        }



                                    />
                                </div>
                            </div>
                        </div>

                        {/* Win Amount */}
                        <div className="col-xl-6 cols">
                            <div className="row">
                                <div className="col-4">
                                    <span className="heading">Edit Win Amount $</span>
                                </div>
                                <div className="col-8">
                                    <input
                                        type="number"
                                        className="content"
                                        name="winAmount"
                                        value={formData.winAmount > 0 ? formData.winAmount : ""}
                                        onChange={(e) =>
                                            straightpayOutCalc(
                                                e.target.value,
                                                item.price,
                                                "win"
                                            )
                                        }

                                    />
                                </div>
                            </div>
                        </div>

                        {/* Total Payout */}
                        <div className="col-xl-6 cols">
                            <div className="row">
                                <div className="col-4">
                                    <span className="heading" title="Amount returned if ticket wins.">
                                        {isDTS() ? `Total ${getTerm("prize")} $` : "Total Payout $"}
                                    </span>
                                </div>
                                <div className="col-8">
                                    <input
                                        type="text"
                                        className="content"
                                        name="totalPayout"
                                        value={formatMoney(formData.totalPayout)}
                                        readOnly
                                        disabled
                                    />
                                </div>
                            </div>
                        </div>

                        {showPtsPurchased && (
                        <div className="col-xl-6 cols">
                            <div className="row">
                                <div className="col-4">
                                    <span
                                        className="heading"
                                        title={
                                            isDTS()
                                                ? `Half-points adjusted on this single ${getTerm("entry", { case: "lower" })}.`
                                                : "Half-points purchased on this straight bet."
                                        }
                                    >
                                        {isDTS() ? getBuyPointsLabel(true) : "PTS Purchased"}
                                    </span>
                                </div>
                                <div className="col-8">
                                    <input
                                        type="text"
                                        className="content"
                                        value={ptsPurchasedLabel}
                                        readOnly
                                        disabled
                                    />
                                </div>
                            </div>
                        </div>
                        )}

                    </div>

                    <div className="table_data mt-3">
                        <Table striped bordered hover responsive>
                            <thead>
                                <tr>
                                    <th>{isMoneyline ? "Edit Desired Price" : "Edit Desired Line"}</th>
                                    <th>Current Game {isMoneyline ? "Price" : "Line"}</th>
                                    <th>Current Game Price</th>
                                    <th>Status</th>
                                    <th className="game_date">Game Date</th>
                                    <th>Teams</th>
                                    {/* <th>Score</th> */}
                                </tr>
                            </thead>
                            <tbody>
                                {item.betType === "Straight" ? (
                                    <tr>
                                        <td>
                                            {isMoneyline ? (
                                                <input
                                                    type="number"
                                                    className="tablecontent"
                                                    value={tableData[0]?.price || ""}
                                                    onChange={(e) =>
                                                        handleTableChange(0, "price", e.target.value)
                                                    }
                                                />
                                            ) : (
                                                <>
                                                    <input
                                                        type="number"
                                                        className="tablecontent"
                                                        value={tableData[0]?.point || ""}
                                                        onChange={(e) =>
                                                            handleTableChange(0, "point", e.target.value)
                                                        }
                                                    /><br />
                                                    {item.pointType}
                                                </>
                                            )}
                                        </td>

                                        <td>
                                            <p>{isMoneyline ? currentPrice : currentPoint}</p>

                                        </td>
                                        <td>
                                            <p>{currentPrice}</p>

                                        </td>
                                        <td>{item.betstatus || "Pending"}</td>
                                        <td>{formatEventKickoff(item.gameTime, { includeTz: true })}</td>
                                        <td>
                                            {item.homeTeam}<br />
                                            {item.awayTeam}
                                        </td>
                                        {/* <td>
                                            <p>{item.score1}</p>
                                            <p>{item.score2}</p>
                                        </td> */}
                                    </tr>
                                ) : (
                                    <></>
                                )}
                            </tbody>
                        </Table>
                    </div>
                </Modal.Body>

                <Modal.Footer>
                    <Button
                        className="close_btn common_bg_full_btn"
                        onClick={() => handleonclickevents("UpdateOrder")}
                    >
                        Submit
                    </Button>

                    <Button
                        className="close_btn common_bg_full_btn"
                        onClick={() => handleonclickevents("OrderCancel")}
                    >
                        Cancel Order
                    </Button>

                </Modal.Footer>
            </Modal>
        </div>
    );
}

export default WagerEditModal;
